import React, { Children, ReactNode } from 'react';
import { Box, Grid, InputAdornment } from '@mui/material';
import {
    consentContractStyles,
    radioButtonData,
} from './create-or-update-consent-contract-helper';
import { Controller } from 'react-hook-form';
import {
    Autocomplete,
    Button,
    DatePicker,
    ErrorMessage,
    FormLabel,
    Radio,
    RadioGroup,
    TextField,
} from '@biq/apex-ui';
import { AutocompleteToField } from './AutocompleteToField';
import CodeMirror from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';
import { MultilineTextField } from 'components';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

interface ConsentContractFormProps {
    isEdit?: boolean;
    control: any;
    errors: any;
    watch: any;
    optionsData: any;
    setValue: any;
    jwtPropertiesValue: any;
    onJwtPropertiesValueChange: any;
    attributeListAsString: string[];
    disablePastStartDate: boolean;
    disablePastEndDate: boolean;
    handleUnsavedChanges?: () => void;
    isExpireContractButtonDisable?: boolean;
    handleExpireContract?: () => void;
    handleExpireButton?: () => void;
}

export const ConsentContractForm = ({
    isEdit = false,
    control,
    errors,
    watch,
    optionsData,
    setValue,
    jwtPropertiesValue,
    onJwtPropertiesValueChange,
    attributeListAsString,
    disablePastStartDate,
    disablePastEndDate,
    handleUnsavedChanges,
    isExpireContractButtonDisable = false,
    handleExpireContract,
    handleExpireButton,
}: ConsentContractFormProps): React.JSX.Element => {
    const datePickerErrorMessage = (errorMessage: string): ReactNode => (
        <ErrorMessage errorMessage={errorMessage} />
    );

    return (
        <Grid sx={{ mx: !isEdit ? '143.5px' : '0px' }}>
            {!isEdit && (
                <Grid sx={consentContractStyles.radioButtonGroupContainer}>
                    <Controller
                        name='share'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <RadioGroup
                                label='Share'
                                required={true}
                                row={true}
                                onChange={field.onChange}
                                value={field.value}
                                error={!!errors.share}
                                errorText={
                                    errors.share ? errors.share.message : ''
                                }
                            >
                                {Children.toArray(
                                    radioButtonData.map((share) => (
                                        <Radio
                                            label={share.label}
                                            value={share.value}
                                        />
                                    ))
                                )}
                            </RadioGroup>
                        )}
                    />
                </Grid>
            )}
            {watch('share') === 'to' ? (
                <Grid sx={consentContractStyles.gridContainer}>
                    <AutocompleteToField
                        optionsData={optionsData}
                        control={control}
                        error={errors.to}
                        setFieldValue={setValue}
                        fieldValue={watch('to')}
                        handleUnsavedChanges={handleUnsavedChanges}
                    />
                </Grid>
            ) : (
                <Grid sx={consentContractStyles.gridContainer}>
                    <Controller
                        name='jwtProperties'
                        control={control}
                        render={(): React.JSX.Element => (
                            <>
                                <FormLabel required label='JWT Properties' />
                                <CodeMirror
                                    value={jwtPropertiesValue}
                                    style={{
                                        ...(errors?.jwtProperties
                                            ? consentContractStyles.jwtPropertiesError
                                            : consentContractStyles.jwtProperties),
                                    }}
                                    height={
                                        consentContractStyles.jwtPropertiesErrorHeight
                                    }
                                    extensions={[json()]}
                                    onChange={onJwtPropertiesValueChange}
                                />
                                {errors?.jwtProperties && (
                                    <Box
                                        style={
                                            consentContractStyles.jwtPropertiesErrorContainer
                                        }
                                    >
                                        <ErrorMessage
                                            errorMessage={
                                                errors?.jwtProperties.message ??
                                                ''
                                            }
                                        />
                                    </Box>
                                )}
                            </>
                        )}
                    />
                </Grid>
            )}
            <Grid sx={consentContractStyles.gridContainer}>
                <MultilineTextField
                    control={control}
                    label='Description'
                    name='description'
                    error={!!errors.description}
                    errorText={
                        errors.description ? errors.description.message : ''
                    }
                    onChange={handleUnsavedChanges}
                />
            </Grid>
            <Grid sx={consentContractStyles.dateAndTimePickerContainer}>
                <Grid sx={consentContractStyles.dateAndTimePickerStyles}>
                    <Controller
                        name='start'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <DatePicker
                                variant='DesktopDateTimePicker'
                                label='Start'
                                size='medium'
                                value={field.value}
                                onChange={(value): void => {
                                    field.onChange(value);
                                    handleUnsavedChanges?.();
                                }}
                                error={!!errors.start}
                                helperText={datePickerErrorMessage(
                                    errors?.start?.message ?? ''
                                )}
                                disablePast={disablePastStartDate}
                            />
                        )}
                    />
                </Grid>
                <Grid
                    sx={{
                        ...consentContractStyles.dividerContainerStyles,
                        alignItems:
                            !!errors.start || !!errors.until ? 'center' : 'end',
                    }}
                >
                    -
                </Grid>
                <Grid sx={consentContractStyles.dateAndTimePickerStyles}>
                    <Controller
                        name='until'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <DatePicker
                                variant='DesktopDateTimePicker'
                                label='End'
                                size='medium'
                                value={field.value}
                                onChange={(value): void => {
                                    field.onChange(value);
                                    handleUnsavedChanges?.();
                                    handleExpireButton?.();
                                }}
                                error={!!errors.until}
                                helperText={datePickerErrorMessage(
                                    errors?.until?.message ?? ''
                                )}
                                disablePast={disablePastEndDate}
                            />
                        )}
                    />
                </Grid>
                {isEdit ? (
                    <Grid
                        sx={{
                            ...consentContractStyles.expireButtonContainer,
                            alignItems:
                                !!errors.start || !!errors.until
                                    ? 'center'
                                    : 'end',
                        }}
                    >
                        <Button
                            variant='text'
                            size='small'
                            startIcon={<AccessTimeIcon />}
                            onClick={handleExpireContract}
                            disabled={isExpireContractButtonDisable}
                        >
                            Expire Contract
                        </Button>
                    </Grid>
                ) : null}
            </Grid>
            <Grid sx={consentContractStyles.gridContainer}>
                <MultilineTextField
                    control={control}
                    label='When'
                    name='when'
                    helperText={
                        'A boolean expression. For example, this WHERE clause in TQL: state == ‘CO’'
                    }
                    error={!!errors.when}
                    errorText={errors.when ? errors.when.message : ''}
                    onChange={handleUnsavedChanges}
                />
            </Grid>
            <Grid sx={consentContractStyles.gridContainer}>
                <Controller
                    name='only'
                    control={control}
                    render={({ field }): React.JSX.Element => (
                        <Autocomplete
                            {...field}
                            options={attributeListAsString}
                            onChange={(__, value): void => {
                                field.onChange(value);
                                handleUnsavedChanges?.();
                            }}
                            multiple
                            sx={consentContractStyles.fieldStyles}
                            renderInput={(params): React.JSX.Element => (
                                <TextField
                                    {...params}
                                    size='medium'
                                    label='Only'
                                    variant='outlined'
                                    InputProps={{
                                        ...params.InputProps,
                                        startAdornment: [
                                            <InputAdornment
                                                key={'searchIcon'}
                                                position='start'
                                            >
                                                <SearchIcon />
                                            </InputAdornment>,
                                            params.InputProps.startAdornment ??
                                                [],
                                        ],
                                    }}
                                />
                            )}
                        />
                    )}
                />
            </Grid>
        </Grid>
    );
};
